import { Suspense, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Badge, Button, Card, CardTitle, useToast } from "@/core/ui";
import { getPuzzleType } from "@/puzzle-types/registry";
import { useBacklogDetail, useSaveDraft, type DefinitionErrorItem } from "./api";
import { ExportMenu } from "./ExportMenu";
import { ImportDialog } from "./ImportDialog";
import { MetadataCard } from "./MetadataCard";
import { ReviewThread } from "./ReviewThread";
import { parseSolution, SolutionCard } from "./SolutionCard";
import { StatusBadge } from "./status";
import { ValidationPanel } from "./ValidationPanel";
import { WorkflowCard } from "./WorkflowCard";

/**
 * The shell an author works in: metadata, the type's own editor, validation, workflow,
 * review and scheduling. The core never knows what kind of puzzle it is (CLAUDE.md §7.1).
 */
export function PuzzleWorkPage() {
  const { slug = "" } = useParams();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const detail = useBacklogDetail(slug);
  const saveDraft = useSaveDraft(slug);

  const [definition, setDefinition] = useState<unknown>(null);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<DefinitionErrorItem[] | null>(null);
  const [solutionText, setSolutionText] = useState("");
  const [testing, setTesting] = useState(false);
  const [importing, setImporting] = useState(false);
  const loadedVersion = useRef<number | null>(null);

  useEffect(() => {
    const version = detail.data?.version;
    if (version && loadedVersion.current !== version.id) {
      loadedVersion.current = version.id;
      setDefinition(version.definition);
      setDirty(false);
    }
  }, [detail.data]);

  if (detail.isPending) return <p className="bt-page-message">{t("app.loading")}</p>;
  if (detail.isError || !detail.data) return <Alert tone="danger">{t("app.error_generic")}</Alert>;

  const { puzzle, version, publication, comments } = detail.data;
  const module = getPuzzleType(puzzle.puzzle_type);
  const parsedSolution = parseSolution(solutionText);

  const save = () => {
    if (!parsedSolution.ok) return;
    saveDraft.mutate(
      { definition, solution: parsedSolution.value },
      {
        onSuccess: (result) => {
          setErrors(result.errors);
          setDirty(false);
          setSolutionText("");
          loadedVersion.current = result.version.id;
          toast(t("backlog.draft_saved"), "success");
        },
        onError: () => toast(t("app.error_generic"), "danger"),
      },
    );
  };

  return (
    <section className="bt-work">
      <div className="bt-work__column">
        <Card>
          <div className="bt-work__header">
            <h1 className="bt-work__title">{puzzle.title}</h1>
            <StatusBadge status={puzzle.status} />
            <Badge tone="info">
              {t(`puzzle_types.${puzzle.puzzle_type}`, { defaultValue: puzzle.puzzle_type })}
            </Badge>
            {version ? (
              <span className="bt-backlog__version">
                {t("backlog.version", { number: version.number })}
              </span>
            ) : null}
            {dirty ? <Badge tone="warning">{t("backlog.unsaved")}</Badge> : null}
          </div>

          <div className="bt-work__toolbar">
            <Button size="sm" variant="outline" onClick={() => setTesting((value) => !value)}>
              {testing ? t("backlog.back_to_editor") : t("backlog.test_as_player")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setImporting(true)}>
              {t("backlog.import.open")}
            </Button>
            <ExportMenu slug={slug} puzzleType={puzzle.puzzle_type} />
            <Button
              className="bt-work__save"
              onClick={save}
              loading={saveDraft.isPending}
              disabled={!parsedSolution.ok || definition === null}
            >
              {t("backlog.save_draft")}
            </Button>
          </div>

          {version?.is_frozen ? <Alert tone="info">{t("backlog.frozen_notice")}</Alert> : null}
          {!version ? <Alert tone="danger">{t("backlog.no_version")}</Alert> : null}
          {!module ? (
            <Alert tone="danger">{t("backlog.unknown_type", { type: puzzle.puzzle_type })}</Alert>
          ) : null}

          {module && definition !== null ? (
            <Suspense fallback={<p className="bt-page-message">{t("app.loading")}</p>}>
              {testing ? (
                <TestAsPlayer module={module} definition={definition} locale={i18n.language} />
              ) : (
                <module.Editor
                  definition={definition}
                  onChange={(next: unknown) => {
                    setDefinition(next);
                    setDirty(true);
                  }}
                  locale={i18n.language}
                />
              )}
            </Suspense>
          ) : null}
          {testing ? <p className="bt-work__test-hint">{t("backlog.test_hint")}</p> : null}
        </Card>

        <ValidationPanel errors={errors} />
        <ReviewThread slug={slug} comments={comments} />
      </div>

      <div className="bt-work__column">
        <MetadataCard puzzle={puzzle} />
        <WorkflowCard puzzle={puzzle} publication={publication} onDefinitionErrors={setErrors} />
        <SolutionCard
          slug={slug}
          puzzleType={puzzle.puzzle_type}
          definition={definition}
          hasSolution={Boolean(version?.has_solution)}
          value={solutionText}
          onChange={setSolutionText}
          canRead={detail.data.can_read_solution}
        />
      </div>

      <ImportDialog
        open={importing}
        onClose={() => setImporting(false)}
        puzzleType={puzzle.puzzle_type}
        onUse={(nextDefinition, solution) => {
          setDefinition(nextDefinition);
          setDirty(true);
          if (solution !== null && solution !== undefined) {
            setSolutionText(JSON.stringify(solution, null, 2));
          }
        }}
      />
    </section>
  );
}

type PuzzleModule = NonNullable<ReturnType<typeof getPuzzleType>>;

/**
 * "Play as a player" preview: purely local. No attempt is created, nothing is submitted and
 * nothing is timed — the core's play loop is untouched.
 */
function TestAsPlayer({
  module,
  definition,
  locale,
}: {
  module: PuzzleModule;
  definition: unknown;
  locale: string;
}) {
  const { t } = useTranslation();
  const [progress, setProgress] = useState<unknown>(() => module.emptyProgress(definition));
  return (
    <div>
      <CardTitle className="bt-visually-hidden">{t("backlog.test_as_player")}</CardTitle>
      <module.Player
        definition={definition}
        progress={progress}
        onProgressChange={setProgress}
        liveFeedback
        locale={locale}
      />
    </div>
  );
}
